import { Component, DestroyRef, inject } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, startWith } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Icon } from 'shared/components/particles/icon/icon';
import { TranslateModule } from '@ngx-translate/core';
import { SITE_ROUTES } from 'shared/enums/routes.enum';

type AuthLayoutContent = {
  titleKey: string;
  descriptionKey: string;
  highlights?: string[];
};

@Component({
  selector: 'app-auth-layout',
  imports: [RouterOutlet, Icon, TranslateModule],
  templateUrl: './auth-layout.html',
  styleUrl: './auth-layout.scss',
})
export class AuthLayout {
  private readonly destroyRef = inject(DestroyRef);

  readonly brandingContentMap: Record<string, AuthLayoutContent> = {
    [SITE_ROUTES.LOGIN]: {
      titleKey: 'authLayoutLoginTitle',
      descriptionKey: 'authLayoutLoginDescription',
    },
    [SITE_ROUTES.REGISTER]: {
      titleKey: 'authLayoutRegisterTitle',
      descriptionKey: 'authLayoutRegisterDescription',
      highlights: [
        'authLayoutRegisterHighlightOne',
        'authLayoutRegisterHighlightTwo',
        'authLayoutRegisterHighlightThree',
      ],
    },
    [SITE_ROUTES.VERIFY_OTP]: {
      titleKey: 'authLayoutVerifyTitle',
      descriptionKey: 'authLayoutVerifyDescription',
    },
    [SITE_ROUTES.FORGET_PASSWORD]: {
      titleKey: 'authLayoutRecoverTitle',
      descriptionKey: 'authLayoutRecoverDescription',
    },
    [SITE_ROUTES.RESET_PASSWORD]: {
      titleKey: 'authLayoutResetTitle',
      descriptionKey: 'authLayoutResetDescription',
    },
  };

  currentContent: AuthLayoutContent = this.brandingContentMap[SITE_ROUTES.LOGIN];

  constructor(private router: Router) {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        startWith(null),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.currentContent = this.getBrandingContent(this.router.url);
      });
  }

  get currentYear(): number {
    return new Date().getFullYear();
  }

  private getBrandingContent(url: string): AuthLayoutContent {
    const matchedRoute = Object.keys(this.brandingContentMap).find((route) => url.includes(route));
    return matchedRoute ? this.brandingContentMap[matchedRoute] : this.brandingContentMap[SITE_ROUTES.LOGIN];
  }
}
